import axios from 'axios'
import TeamList from '../../assets/teams'
import { TeamMashup } from '@/services/api'

/**
 * Find the url of the image with the largest resolution
 *
 * @param {Array=} images The `*.cuts` node from Homebase data
 */
const getLargestResolutionImageUrl = (images = []) => {
  if (!images || !images.length) return ''

  return images.sort((a, b) => {
    const aSize = (a.width * a.height)
    const bSize = (b.width * b.height)

    return aSize === bSize ? 0 : bSize - aSize
  })[0].src.replace(/^https/, 'http').replace('securea', 'mlb')
}

/**
 * Get the `value`s of all `keywords` with a specific `keywordType`
 *
 * @param {Array=} keywords The `keywordsAll` node from the Homebase data
 * @param {?string=} keywordType The `type` of the `keywordsAll` to find
 * @param {Array=} defaults The default values to return if no keywords are found
 */
const getKeywords = (keywords = [], keywordType = null, defaults = []) => {
  const results = keywords.reduce((list, keywordObj) => {
    const { type, value } = keywordObj

    if (type === keywordType) {
      list.push(value)
    }

    return list
  }, [])

  return results.length === 0 ? defaults : results
}

/**
 * Convenience function for getting all `device` keywords. Returns
 * `['phone', 'tablet']` by default.
 *
 * @param {Array} keywords The `keywordsAll` node from the Homebase data
 */
const getDevices = (keywords) => getKeywords(keywords, 'device', ['phone', 'tablet'])

/**
 * Convenience function for getting all `platform` keywords. Returns
 * `['ios', 'android']` by default.
 *
 * @param {Array} keywords The `keywordsAll` node from the Homebase data
 */
const getPlatforms = (keywords) => getKeywords(keywords, 'platform', ['ios', 'android'])

/**
 * Create input model from Homebase images which is maintained
 * in the output until a user has uploaded an image via Cloudinary
 *
 * @param {?Array=} cuts The `cuts` from the Homebase source
 */
const createImageInputModel = (cuts = []) => {
  if (!cuts.length) {
    return {}
  }

  const keys = ['30X30', '60X60', '90X90', '280X155', '318X176', '320X180', '477X264', '560X310']

  return keys.reduce((prev, key) => {
    const cut = (cuts.find((cut) => cut.dimensions === key.toLowerCase()) || {}).src

    prev[key] = (cut || '').replace(/^https/, 'http').replace('securea', 'mlb')

    return prev
  }, {})
}

/**
 * Create output model used by client to render images.
 *
 * @param {string} publicId The `publicId` returned from the Cloudinary response
 */
const createImageOutputModel = (publicId) => {
  if (!publicId) {
    return {}
  }

  const keys = ['30X30', '60X60', '90X90', '280X155', '318X176', '320X180', '477X264', '560X310']
  const template = `https://img.mlbstatic.com/mlb-mobile/t_{transform}/${publicId}`

  return keys.reduce((obj, key) => {
    obj[key] = template.replace('{transform}', key)

    return obj
  }, {})
}

/**
 * Get team metadata by searching `field` for `value`
 *
 * @param {string} field The field to search for (i.e. `id`)
 * @param {string} value The value to find (i.e. `111`)
 */
export const getTeamObjectBy = (field, value) => {
  return Object.assign({}, TeamList.find((team) => team[field] === value))
}

/**
 * Get the label text for the `statsLabel` node
 *
 * @param {number} statsMode The statsMode (as specified by the config)
 */
export const getStatsLabel = (statsMode) => {
  return ({
    0: 'Spring Training Leaders',
    1: 'Regular Season Leaders',
    2: 'Postseason Leaders',
  }[statsMode] || 'Postseason Leaders')
}

/**
 * Get the label text for the `standingsLabel` node
 *
 * @param {string} fileCode The fileCode of the team whose standings label is returned
 */
export const getStandingsLabel = (fileCode) => {
  const { division } = getTeamObjectBy('fileCode', fileCode)

  if (!division) return ''

  return `${division} Standings`
}

/**
 * Get the output model for a given component from its data object
 *
 * @param {Object} component The component data object created by adding a new object to the store
 * @param {Object=} options The options to pass to the model function
 */
export const getOutputModelForComponent = (component, options = {}) => {
  return ({
    'short-content': getShortContentOutputModel,
    'application-promo': getApplicationPromoOutputModel,
  })[component.type](component, options)
}

/**
 * Create the output model from the `ShortContent` component data
 *
 * @param {Object} shortContent The `ShortContent` component data object
 */
const getShortContentOutputModel = (shortContent) => {
  const { id, url: URL, imagePublicId, hbImages, type, title, popup } = shortContent
  const images = hbImages || createImageOutputModel(imagePublicId)

  return {
    id,
    URL,
    images,
    type,
    title,
    popup,
  }
}

/**
 * Create the output model from the `ApplicationPromo` component data
 *
 * @param {Object} applicationPromo The `ApplicationPromo` component data object
 * @param {Object=} options The options to generate the appropriate output model
 */
const getApplicationPromoOutputModel = (applicationPromo, options = {}) => {
  const { platform } = options
  const {
    id,
    hbImages,
    imagePublicId,
    iosPackage,
    androidPackage,
    iosUrl: storeURL,
    androidUrl: storeURLAndroid,
    type,
    intent,
    title,
  } = applicationPromo
  const images = hbImages || createImageOutputModel(imagePublicId)

  return {
    id,
    images,
    package: platform === 'ios' ? iosPackage : androidPackage,
    type,
    storeURL,
    storeURLAndroid,
    intent,
    title,
  }
}

/**
 * Create the `ShortContent` input model from the Homebase data
 *
 * @param {Object} obj The Homebase data object
 */
export const getShortContentInputModel = (obj, { locale }) => {
  const {
    id,
    headline: title,
    url,
    popup,
    photos = [{}],
    keywordsAll: keywords = [],
  } = obj
  const { cuts = [] } = photos[0] || {}

  return {
    id,
    type: 'short-content',
    title,
    url,
    popup: popup === 'Y' ? true : false,
    imageUrl: getLargestResolutionImageUrl(cuts),
    hbImages: createImageInputModel(cuts),
    devices: getDevices(keywords),
    platforms: getPlatforms(keywords),
    locale,
  }
}

/**
 * Create the `ApplicationPromo` input model from the Homebase data
 *
 * @param {Object} obj The Homebase data object
 */
export const getApplicationPromoInputModel = (obj, { locale }) => {
  const {
    id,
    headline: title,
    blurb = '',
    urlIos: iosUrl,
    urlAndroid: androidUrl,
    packageIos: iosPackage,
    packageAndroid: androidPackage,
    intent,
    external,
    photos = [{}],
    keywordsAll: keywords = [],
  } = obj
  const { cuts = [] } = photos[0] || {}

  return {
    id,
    type: 'application-promo',
    title,
    blurb,
    iosUrl,
    androidUrl,
    iosPackage,
    androidPackage,
    intent,
    popup: external === 'Y' ? true : false,
    imageUrl: getLargestResolutionImageUrl(cuts),
    hbImages: createImageInputModel(cuts),
    devices: getDevices(keywords),
    platforms: getPlatforms(keywords),
    locale,
    cloudinary: true,
  }
}

/**
 * Get the input conversion function for the given type. Returns a no-op
 * if there is no function for the given type.
 *
 * @param {string} type The Homebase `type` for the type to model
 */
export const getInputConversionFunction = (type) => {
  const fns = {
    shortcontent: getShortContentInputModel,
    applicationPromo: getApplicationPromoInputModel,
  }

  return fns[type] || (() => {
  })
}

/**
 * Get the config data from Homebase based on the `fileCode`
 *
 * @param {string} fileCode The fileCode of the team
 */
export const getHomebaseConfigData = async (fileCode) => {
  try {
    return (await axios.get(`${process.env.VUE_APP_MLB_BASE_URL}/gen/content/tag/v1/key-value/atbat-configuration/${fileCode}/general-v1.json`, {
      headers: {
        Cookie: "betaaccess=true;",
      },
    })).data
  } catch (e) {
    return {}
  }
}

/**
 * Get the list of ids from Homebase objects for a given `taxonomy`, based on `locale` and `teamId`
 *
 * @param {Object} obj The parameters needed to construct the url
 * @param {string} obj.type The taxonomy type: `'insider'` or `'moreFrom'`
 * @param {string} obj.locale The locale: `'en'` or `'es'`
 * @param {number} obj.teamId The teamId to filter the taxonomy on
 */
export const getHomebaseIdList = async ({ type, locale, teamId }) => {
  try {
    const url = `${process.env.VUE_APP_SEARCH_API_BASE_URL}/svc/search/v2/graphql/persisted/query/mlb/query/atbat/mashup/contentlist?variables={"hbtax":"atbat_team_${type}_${locale}","team_id":"${teamId}"}`
    const response = await axios.get(url)

    return response.data.data.query.hits[0].hit.list.map((x) => x.id)
  } catch (e) {
    return []
  }
}

/**
 * Get the data for a list of Homebase content ids
 *
 * @param {Array} ids The content ids to fetch
 */
export const getHomebaseIdData = async (ids) => {
  try {
    const url = `${process.env.VUE_APP_SEARCH_API_BASE_URL}/svc/search/v2/graphql/persisted/query/mlb/query/atbat/mashup?variables={"ids":[${ids.join(',')}]}`
    const response = await axios.get(url)

    return response.data.data.query.hits
  } catch (e) {
    return []
  }
}

export const getDataFromStorage = async (fileCode) => {
  try {
    return await TeamMashup.getMashup(fileCode)
  } catch (e) {
    return undefined
  }
}
